import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

type CardVariant = 'default' | 'glass' | 'glow' | 'premium' | 'pricing' | 'pricing-popular';

interface CardProps {
  children: ReactNode;
  variant?: CardVariant;
  hover?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  rounded?: 'lg' | 'xl' | '2xl' | '3xl';
  animate?: boolean;
  delay?: number;
  className?: string;
}

const variants: Record<CardVariant, string> = {
  default: 'bg-brand-blue border-brand-lightBlue/30',
  glass: 'bg-brand-blue/60 backdrop-blur-xl border-brand-lightBlue/30',
  glow: 'bg-brand-blue border-brand-yellow/30 shadow-[0_0_40px_-10px_rgba(255,215,0,0.2)]',
  premium: `
    bg-gradient-to-br from-brand-blue via-brand-blue/95 to-brand-navy 
    border-brand-lightBlue/30 
    shadow-[0_4px_24px_-2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.02)_inset]
    backdrop-blur-xl
  `,
  pricing: 'bg-brand-blue/80 backdrop-blur-xl border-brand-lightBlue/30',
  'pricing-popular': `
    bg-gradient-to-br from-brand-blue via-brand-blue/95 to-brand-navy 
    border-brand-yellow/50 
    shadow-[0_0_40px_-10px_rgba(255,215,0,0.3)]
  `,
};

const paddings: Record<NonNullable<CardProps['padding']>, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
  xl: 'p-10',
};

const roundedStyles: Record<NonNullable<CardProps['rounded']>, string> = {
  lg: 'rounded-2xl',
  xl: 'rounded-3xl',
  '2xl': 'rounded-[2rem]',
  '3xl': 'rounded-[2.5rem]',
};

export function Card({
  children,
  variant = 'glass',
  hover = false,
  padding = 'md',
  rounded = 'lg',
  animate = true,
  delay = 0,
  className = '',
}: CardProps) {
  const MotionComponent = animate ? motion.div : 'div';
  const animationProps = animate
    ? {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-50px' },
        transition: { duration: 0.5, delay },
        whileHover: hover ? { y: -8, scale: 1.02 } : undefined,
      }
    : {};

  return (
    <MotionComponent
      {...animationProps}
      className={`
        relative border overflow-hidden
        ${roundedStyles[rounded]}
        ${variants[variant]}
        ${paddings[padding]}
        ${
          hover
            ? 'transition-all duration-500 hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)] cursor-pointer hover:border-brand-yellow/30'
            : ''
        }
        ${className}
      `}
    >
      {/* Top highlight for premium effect */}
      {(variant === 'premium' || variant === 'glow' || variant === 'pricing-popular') && (
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/30 to-transparent pointer-events-none" />
      )}
      
      {children}
    </MotionComponent>
  );
}
